/**
 * Typed HTTP client for the blog-api Lambda.
 *
 * Frontend client for the blog-api Lambda.
 */
import { API_BASE_URL as BASE } from '@/lib/apiBase';

interface ApiError {
  error: string;
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`);
  const body = await res.json() as T | ApiError;
  if (!res.ok) throw new Error((body as ApiError).error ?? `HTTP ${res.status}`);
  return body as T;
}

async function request<T>(path: string, options: RequestInit, token?: string): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${BASE}${path}`, { ...options, headers });
  const body = await res.json() as T | ApiError;
  if (!res.ok) throw new Error((body as ApiError).error ?? `HTTP ${res.status}`);
  return body as T;
}

// ── Public API ─────────────────────────────────────────────────────────────

export interface ApiPostSummary {
  slug: string;
  title: string;
  description: string;
  date: string;
  author: string;
  category: string;
  tags: string[];
  featured: boolean;
  status: 'draft' | 'published' | 'deleted';
  readingTime: string;
}

export interface ApiPostDetail extends ApiPostSummary {
  content: string;
}

export const blogApiClient = {
  listPosts(): Promise<{ posts: ApiPostSummary[] }> {
    return get('/posts');
  },

  getPost(slug: string): Promise<ApiPostDetail> {
    return get(`/posts/${slug}`);
  },

  // ── Admin API ────────────────────────────────────────────────────────────

  login(password: string, totp: string): Promise<{ token: string }> {
    return request('/auth', { method: 'POST', body: JSON.stringify({ password, totp }) });
  },

  checkSession(token: string): Promise<{ ok: boolean }> {
    return request('/auth/me', { method: 'GET' }, token);
  },

  adminListPosts(token: string): Promise<{ posts: ApiPostSummary[] }> {
    return request('/admin/posts', { method: 'GET' }, token);
  },

  adminGetPost(token: string, slug: string): Promise<ApiPostDetail> {
    return request(`/admin/posts/${slug}`, { method: 'GET' }, token);
  },

  adminCreatePost(token: string, payload: Omit<ApiPostDetail, 'readingTime'>): Promise<{ slug: string }> {
    return request('/admin/posts', { method: 'POST', body: JSON.stringify(payload) }, token);
  },

  adminUpdatePost(token: string, slug: string, patch: Partial<ApiPostDetail>): Promise<{ slug: string }> {
    return request(`/admin/posts/${slug}`, { method: 'PATCH', body: JSON.stringify(patch) }, token);
  },

  adminDeletePost(token: string, slug: string): Promise<{ slug: string; deleted: boolean }> {
    return request(`/admin/posts/${slug}`, { method: 'DELETE' }, token);
  },

  adminPermanentDeletePost(token: string, slug: string): Promise<{ slug: string; deleted: boolean }> {
    return request(`/admin/posts/${slug}?permanent=true`, { method: 'DELETE' }, token);
  },

  adminFetchUrl(token: string, url: string): Promise<{ text: string }> {
    return request(`/admin/fetch-url?url=${encodeURIComponent(url)}`, { method: 'GET' }, token);
  },
};
